import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Sheet,
  Avatar,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Chip,
} from "@mui/joy";
import { Delete, ChevronRight } from "@mui/icons-material";

import AddIcon from "@mui/icons-material/Add";

import { supabase } from "../../supabaseClient";
import { fetchProfilePicture, getInitials, formatStudentName, getStudentBirthdayStatus } from "../../utils";
import { format, parseISO } from "date-fns";
import { useModal } from "../../context/ModalContext";
import { useToast } from "../../context/ToastContext";

import { Student as DBStudent, GradeLevelLabels } from "../../types";
import EnrollStudentModal from "../modals/EnrollStudentModal";

type StudentWithEnrollmentData = DBStudent & { 
  profile_picture?: string | null;
  enrollment_type?: 'full' | 'half';
};

interface ClassStudentsTableProps {
  classId: string | undefined;
  onEnrolledCountChange?: (count: number) => void;
}

const ClassStudentsTable: React.FC<ClassStudentsTableProps> = ({ 
  classId, 
  onEnrolledCountChange 
}) => {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { showToast } = useToast();
  const [students, setStudents] = useState<StudentWithEnrollmentData[]>([]);
  const [classSupportsHalfDay, setClassSupportsHalfDay] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (onEnrolledCountChange) {
      onEnrolledCountChange(students.length);
    }
  }, [students, onEnrolledCountChange]);

  const fetchStudents = useCallback(async () => {
    if (!classId) return;

    try {
      const { data, error } = await supabase
        .from("classes_students")
        .select(`
          enrollment_type,
          student:student_id (*)
        `)
        .eq("class_id", classId);

      // Also fetch class details to check if it supports half-day
      const { data: classData, error: classError } = await supabase
        .from("classes")
        .select("schedule")
        .eq("id", classId)
        .single();

      if (classError) throw classError;

      // Check if any day in the schedule supports half-day
      const supportsHalfDay = classData?.schedule && 
        Object.values(classData.schedule).some((day: any) => day.supportsHalf);
      setClassSupportsHalfDay(!!supportsHalfDay);

      if (error) throw error;

      const studentsWithPictures = await Promise.all(
        (data as unknown as { student: DBStudent; enrollment_type: 'full' | 'half' }[]).map(async (row) => {
          const student = row.student;
          
          return {
            ...student,
            profile_picture: await fetchProfilePicture("students", student.profile_picture_id),
            enrollment_type: row.enrollment_type || 'full',
          } as StudentWithEnrollmentData;
        })
      );

      setStudents(studentsWithPictures);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    fetchStudents();
  }, [classId, fetchStudents]);

  // Auto-refresh every 30 seconds to show updated check-in status
  useEffect(() => {
    if (!classId) return;
    
    const interval = setInterval(() => {
      fetchStudents();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [classId, fetchStudents]);

  const handleRemoveStudent = async (studentId: string) => {
    if (!classId) return;

    try {
      const { error } = await supabase
        .from("classes_students")
        .delete()
        .eq("class_id", classId)
        .eq("student_id", studentId);

      if (error) throw error;

      setStudents(students.filter((student) => student.id !== studentId));
      showToast("Student unenrolled successfully!", "success", "solid");
    } catch (error) {
      console.error("Error removing student:", error);
      showToast("Error unenrolling student.", "danger", "solid");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const handleEnrollStudent = () => {
    if (!classId) return;
    openModal(
      <EnrollStudentModal 
        classId={classId} 
        onEnrollmentUpdated={fetchStudents} 
      />
    );
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          startDecorator={<AddIcon />}
          onClick={handleEnrollStudent}
        >
          Enroll Student
        </Button>
      </Box>
      <Sheet
        variant="outlined"
        sx={{
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        {students.length > 0 ? (
          <Table
            aria-label="students table"
            stickyHeader
            hoverRow
            sx={{
              "--TableCell-headBackground": "var(--joy-palette-background-level1)",
              "--Table-headerUnderlineThickness": "1px",
              "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
              "--TableCell-paddingY": "4px",
              "--TableCell-paddingX": "8px",
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 300, padding: "12px 6px" }}>Name</th>
                <th style={{ width: 100, padding: "12px 6px" }}>Grade</th>
                {classSupportsHalfDay && (
                  <th style={{ width: 100, padding: "12px 6px" }}>Type</th>
                )}
                <th style={{ width: 100, padding: "12px 6px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
              <tr key={student.id}>
                <td>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={student.profile_picture || undefined}
                      sx={{ width: 32, height: 32 }}
                    >
                      {!student.profile_picture ? getInitials(student) : null}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography
                          level="body-sm"
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/students/${student.id}`)}
                        >
                          {formatStudentName(student)}
                        </Typography>
                        
                        {/* Birthday Badge */}
                        {(() => {
                          const birthdayStatus = getStudentBirthdayStatus(student.date_of_birth);
                          if (birthdayStatus) {
                            return (
                              <Tooltip title={`Birthdate: ${format(parseISO(student.date_of_birth || ""), "MMMM do, yyyy")}`}>
                                <Chip
                                  variant="soft"
                                  color="success"
                                  size="sm"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    lineHeight: 1,
                                  }}
                                >
                                  🎂 {birthdayStatus === 'today' ? 'Birthday Today!' : 'Birthday Week!'}
                                </Chip>
                              </Tooltip>
                            );
                          }
                          return null;
                        })()}
                        
                        {/* Half Day Badge */}
                        {student.enrollment_type === 'half' && (
                          <Tooltip title="Half Day Enrollment">
                            <Chip
                              variant="soft"
                              color="primary"
                              size="sm"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                lineHeight: 1,
                              }}
                            >
                              🕐 Half Day
                            </Chip>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </td>
                <td>
                  <Typography level="body-sm">{GradeLevelLabels[student.grade_level as import("../../types/Grade").GradeLevel] || "Not Set"}</Typography>
                </td>
                {classSupportsHalfDay && (
                  <td>
                    <Typography level="body-sm">
                      {student.enrollment_type === 'half' ? (
                        <span style={{ color: 'var(--joy-palette-primary-500)' }}>🕐 Half Day</span>
                      ) : (
                        'Full Day'
                      )}
                    </Typography>
                  </td>
                )}
                <td>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Unenroll Student from Class">
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => handleRemoveStudent(student.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Student Details">
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => navigate(`/students/${student.id}`)}
                      >
                        <ChevronRight />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
          <Typography level="body-lg" color="neutral" sx={{ mb: 1 }}>
            No Students Enrolled
          </Typography>
          <Typography level="body-sm" color="neutral">
            This class currently has no enrolled students. Use the "Enroll Student" button above to add students.
          </Typography>
        </Box>
      )}
      </Sheet>
    </>
  );
};

export default ClassStudentsTable; 